import { useTranslation } from 'react-i18next';
import tw, { css, theme } from 'twin.macro';

import { Button } from './Button';
import { Spacer } from './Spacer';
import { CloseIcon } from '@icons/CloseIcon';
import { useWhatsNew } from '@app/hooks/useWhatsNew';

const Container = tw.div`fixed right-8 bottom-8 w-[372px] bg-neutral-background z-[9999] shadow-modal rounded-lg overflow-hidden`;
const Content = tw.div`w-full p-4`;
const Flex = tw.div`flex items-center justify-between`;
const FlexEnd = tw(Flex)`justify-end`;
const ImageContainer = tw.div`w-full h-[200px] flex items-center justify-center overflow-hidden relative bg-contain bg-no-repeat`;
const Title = tw.h3`text-lg text-text-and-icon text-left`;
const WhatNewLabel = tw.span`text-text-and-icon-muted text-sm font-bold`;
const WhatNewDate = tw.span`text-text-and-icon-muted text-xs`;
const Description = tw.div`text-sm text-left text-text-and-icon-muted [&>p]:text-text-and-icon`;
const StyledButton = tw(
  Button.Primary
)`w-[65px] h-[30px] px-4 text-[13px] bg-accent-2 border-accent-2 text-text-and-icon-invert`;
const CloseButton = tw(
  StyledButton
)`bg-[rgba(25, 27, 32, 0.3)] absolute right-4 top-4 w-6 h-6 p-0 flex items-center justify-center`;

const descriptionStyles = css`
  p,
  span {
    text-align: left;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    color: ${theme`colors.text-and-icon-muted`};
  }
`;

export const WhatsNew = ({
  isVisible,
  setIsVisible,
}: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
}) => {
  const { t } = useTranslation();
  const {
    userLatestWhatsNew,
    userWhatsNew,
    isLatestNewLoading,
    isUserWhatsNewLoading,
    mutateUserWhatsNew,
    hasSeenLatestVersion,
  } = useWhatsNew();

  const handleClose = () => {
    setIsVisible(false);

    if (!userWhatsNew?.length && userLatestWhatsNew?.version) {
      mutateUserWhatsNew({ version: userLatestWhatsNew.version });
    }
  };

  if (isLatestNewLoading || isUserWhatsNewLoading) {
    return null;
  }

  if (userLatestWhatsNew && (isVisible || !hasSeenLatestVersion)) {
    return (
      <Container data-testid='whats-new'>
        <ImageContainer style={{ backgroundImage: `url(${userLatestWhatsNew.image_url})` }}>
          <CloseButton onClick={handleClose} data-testid='whats-new-close'>
            <CloseIcon tw='fill-text-and-icon cursor-pointer' />
          </CloseButton>
        </ImageContainer>
        <Content>
          <Flex>
            <WhatNewLabel>{t('insights.whats_new.card_title')}</WhatNewLabel>
            <WhatNewDate>
              {new Date(userLatestWhatsNew.publish_date).toLocaleDateString()}
            </WhatNewDate>
          </Flex>
          <Spacer direction='vertical' size='md' />
          <Title>{userLatestWhatsNew.title}</Title>
          <Spacer direction='vertical' size='xs' />
          <Description
            data-testid='whats-new-description'
            css={[descriptionStyles]}
            dangerouslySetInnerHTML={{
              __html: userLatestWhatsNew.description,
            }}
          />

          <Spacer direction='vertical' size='xxxl' />
          <FlexEnd>
            <StyledButton data-testid='whats-new-cta' onClick={handleClose}>
              {t('common.ok')}
            </StyledButton>
          </FlexEnd>
        </Content>
      </Container>
    );
  }
  return null;
};
